import { computed, ComputedRef, ref, watch } from 'vue'
import { useStore } from '@web/store'
import { specialProjectId } from '@web/common/ExternalConfig/getters'
import {
  hasConsent,
  acceptConsent,
  migrateGuestConsent
} from '@web/platform.v3/composables/cookieConsentStorage'

export interface UseCookieConsentDeps {
  getUserId?: () => number | string | null | undefined
  // specialProjectId() типизирован как 'any' | 'take', но на проектах, где бэк
  // projectId не отдаёт (например iqface), фактически возвращает undefined.
  getProjectId?: () => 'any' | 'take' | undefined
}

export interface UseCookieConsent {
  isVisible: ComputedRef<boolean>
  accept: () => void
}

// Ревизия для пересчёта isVisible после записи в хранилище: сами cookie и
// localStorage не реактивны. Модульная, а не локальная — композабл вызывается
// из нескольких мест (CookieConsent.vue и плашка оплаты), и accept() в одном
// инстансе обязан поднять isVisible у всех остальных, а не только у своего.
// Тот же паттерн, что у dismissTick и expanded в usePaymentFailureWarning.ts.
const revision = ref(0)

export function useCookieConsent (deps: UseCookieConsentDeps = {}): UseCookieConsent {
  // useStore() можно вызывать только внутри setup(). Если зависимость передана,
  // store не нужен — не трогаем его вовсе (паттерн usePersonalSelection).
  const store = deps.getUserId ? null : useStore()

  const getUserId = deps.getUserId ?? ((): number | string | null | undefined => store?.getters['user/user']?.id)
  const getProjectId = deps.getProjectId ?? specialProjectId

  // Плашка нужна только на Any — включаем по явному projectId === 'any'.
  // На Take и на проектах без projectId (iqface) не показываем.
  // projectId приходит из внешнего конфига, который в main.ts загружается
  // до mount() — значит при первом рендере значение уже верное, мигания нет.
  const isEnabledForProject = computed<boolean>(() => getProjectId() === 'any')

  const userId = computed<number | string | null | undefined>(() => getUserId())

  const isVisible = computed<boolean>(() => {
    if (!isEnabledForProject.value) {
      return false
    }
    // Читаем revision, чтобы computed зависел от неё и пересчитывался после
    // записи в cookie/localStorage (сами по себе они не реактивны).
    const trackedRevision = revision.value
    return trackedRevision >= 0 && !hasConsent(userId.value)
  })

  function accept (): void {
    if (!isEnabledForProject.value) {
      return
    }
    acceptConsent(userId.value)
    revision.value += 1
  }

  // Пользователь авторизовался — переносим гостевое согласие на его ключ.
  watch(userId, (value) => {
    if (!value || !isEnabledForProject.value) {
      return
    }
    migrateGuestConsent(value)
    revision.value += 1
  }, { immediate: true })

  return { isVisible, accept }
}
